import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, RefreshCw, Package, Clock, 
  Phone, Mail, MapPin, LogOut, Edit2, Save, X,
  Plus, Trash2, ShoppingBag, Lock, Tag, Sparkles, Star,
  Upload, CreditCard, Banknote, ImagePlus, Users
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { formatPrice } from '../lib/format';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPage = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const additionalImagesRef = useRef(null);
  const newProductImageRef = useRef(null);
  const newProductAdditionalRef = useRef(null);
  
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('adminToken') || '');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);

  // Data state
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [promoCodes, setPromoCodes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ products: 0, orders: 0, new_orders: 0, bestsellers: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [productFilter, setProductFilter] = useState('hookah');

  // Edit product state
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saveLoading, setSaveLoading] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  // New product state
  const [showNewProduct, setShowNewProduct] = useState(false);
  const [newProductForm, setNewProductForm] = useState({
    name_hu: '', name_en: '', description_hu: '', description_en: '',
    price: '', brand: '', height: '', category: 'hookah', subcategory: null,
    inStock: true, isBestseller: false, isSale: false, salePrice: '', saleEndDate: '',
    image: '', images: [],
    // Specs for hookah/set
    spec_height: '', spec_brand: '', spec_material: '', 
    spec_water_tank: '', spec_hose_type: '',
  });

  // Promo code state
  const [showNewPromo, setShowNewPromo] = useState(false);
  const [newPromoForm, setNewPromoForm] = useState({
    code: '', discount_type: 'fixed', discount_value: '', max_uses: 0, min_order: '', expires_at: '',
  });

  const statusLabels = {
    new: { hu: 'Új', en: 'New', color: 'bg-blue-600' },
    processing: { hu: 'Feldolgozás', en: 'Processing', color: 'bg-yellow-600' },
    shipped: { hu: 'Elküldve', en: 'Shipped', color: 'bg-amber-600' },
    completed: { hu: 'Teljesítve', en: 'Completed', color: 'bg-green-600' },
    cancelled: { hu: 'Törölve', en: 'Cancelled', color: 'bg-red-600' },
  };

  const deliveryLabels = {
    pickup: { hu: 'Átvétel', en: 'Pickup' },
    domestic: { hu: 'Házhozszállítás', en: 'Delivery' },
    international: { hu: 'Nemzetközi', en: 'International' },
  };

  const paymentLabels = {
    cash_on_delivery: { hu: 'Átvételkor', en: 'On Delivery' },
    prepayment: { hu: 'Előre', en: 'Prepayment' },
  };

  const categoryLabels = {
    hookah: { hu: 'Vízipipa', en: 'Hookah' },
    set: { hu: 'Készlet', en: 'Set' },
    accessory: { hu: 'Kiegészítő', en: 'Accessory' },
  };

  // Product type options for the admin form. Accessory items map to a category + subcategory
  // so they land in the correct filter tab on the Accessories page.
  const productTypes = [
    { value: 'hookah', category: 'hookah', subcategory: null, label: { hu: 'Vízipipa', en: 'Hookah' } },
    { value: 'set', category: 'set', subcategory: null, label: { hu: 'Készlet', en: 'Set' } },
    { value: 'acc_bowl', category: 'accessory', subcategory: 'bowl', label: { hu: 'Dohánytölcsérek', en: 'Tobacco Bowls' } },
    { value: 'acc_coalholder', category: 'accessory', subcategory: 'coalholder', label: { hu: 'Széntartó konzolok', en: 'Coal Holder Consoles' } },
    { value: 'acc_coalcarrier', category: 'accessory', subcategory: 'coalcarrier', label: { hu: 'Szén hordozók', en: 'Coal Carriers' } },
    { value: 'acc_mouthpiece', category: 'accessory', subcategory: 'mouthpiece', label: { hu: 'Szipkák', en: 'Mouthpieces' } },
    { value: 'acc_handmade', category: 'accessory', subcategory: 'handmade', label: { hu: 'Kézműves dohánytartók/szipkák', en: 'Handcrafted Bowls/Mouthpieces' } },
    { value: 'acc_tip', category: 'accessory', subcategory: 'tip', label: { hu: 'Szívóvégek', en: 'Hose Tips' } },
    { value: 'acc_tongs', category: 'accessory', subcategory: 'tongs', label: { hu: 'Csipeszek', en: 'Tongs' } },
    { value: 'acc_heatcap', category: 'accessory', subcategory: 'heatcap', label: { hu: 'Hőtartó sapkák', en: 'Heat Caps' } },
    { value: 'acc_hose', category: 'accessory', subcategory: 'hose', label: { hu: 'Szívócsövek', en: 'Hoses' } },
    { value: 'acc_fork', category: 'accessory', subcategory: 'fork', label: { hu: 'Dohányvilla és alufólia-lyukasztó', en: 'Tobacco Fork & Foil Punch' } },
    { value: 'acc_flask', category: 'accessory', subcategory: 'flask', label: { hu: 'Víztartályok', en: 'Water Tanks' } },
    { value: 'acc_bag', category: 'accessory', subcategory: 'bag', label: { hu: 'Táskák', en: 'Bags' } },
    { value: 'acc_cleaner', category: 'accessory', subcategory: 'cleaner', label: { hu: 'Tisztítók', en: 'Cleaners' } },
    { value: 'acc_burner', category: 'accessory', subcategory: 'burner', label: { hu: 'Szénizzítők', en: 'Coal Burners' } },
    { value: 'acc_other', category: 'accessory', subcategory: 'other', label: { hu: 'Egyéb', en: 'Other' } },
  ];

  const getTypeValue = (category, subcategory) => {
    if (category === 'accessory') {
      const match = productTypes.find((o) => o.category === 'accessory' && o.subcategory === subcategory);
      return match ? match.value : 'acc_other';
    }
    return category || 'hookah';
  };

  const handleTypeChange = (value, isNew = false) => {
    const opt = productTypes.find((o) => o.value === value);
    if (!opt) return;
    if (isNew) {
      setNewProductForm((prev) => ({ ...prev, category: opt.category, subcategory: opt.subcategory }));
    } else {
      setEditForm((prev) => ({ ...prev, category: opt.category, subcategory: opt.subcategory }));
    }
  };

  // Check auth
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          await axios.get(`${API}/auth/verify`, { headers: { Authorization: `Bearer ${token}` } });
          setIsAuthenticated(true);
        } catch { localStorage.removeItem('adminToken'); setToken(''); setIsAuthenticated(false); }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError(''); setLoginLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, loginForm);
      setToken(res.data.token);
      localStorage.setItem('adminToken', res.data.token);
      setIsAuthenticated(true);
    } catch { setLoginError(language === 'hu' ? 'Hibás adatok' : 'Invalid credentials'); }
    finally { setLoginLoading(false); }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken'); setToken(''); setIsAuthenticated(false);
  };

  useEffect(() => { if (isAuthenticated) fetchData(); }, [isAuthenticated, filterStatus]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [ordersRes, productsRes, statsRes, promoRes, customersRes] = await Promise.all([
        axios.get(`${API}/orders`, { headers, params: filterStatus !== 'all' ? { status: filterStatus } : {} }),
        axios.get(`${API}/products`),
        axios.get(`${API}/stats`, { headers }),
        axios.get(`${API}/promo-codes`, { headers }),
        axios.get(`${API}/admin/customers`, { headers })
      ]);
      setOrders(ordersRes.data); setProducts(productsRes.data);
      setStats(statsRes.data); setPromoCodes(promoRes.data);
      setCustomers(customersRes.data);
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.patch(`${API}/orders/${orderId}/status?status=${newStatus}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
      if (selectedOrder?.id === orderId) setSelectedOrder(prev => ({ ...prev, status: newStatus }));
    } catch (e) { console.error('Error:', e); }
  };

  // Image upload
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await axios.post(`${API}/upload`, formData, {
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
    });
    // Store a RELATIVE path (host-agnostic) so images work in both preview and production
    return res.data.url;
  };

  const handleMainImageUpload = async (file, isNew = false) => {
    if (!file) return;
    setUploadingImage(true);
    try {
      const url = await uploadImage(file);
      if (isNew) setNewProductForm(prev => ({ ...prev, image: url }));
      else setEditForm(prev => ({ ...prev, image: url }));
    } catch (e) { console.error('Error:', e); }
    finally { setUploadingImage(false); }
  };

  const handleAdditionalImageUpload = async (files, isNew = false) => {
    if (!files.length) return;
    setUploadingImage(true);
    try {
      const urls = await Promise.all(Array.from(files).map(f => uploadImage(f)));
      if (isNew) {
        setNewProductForm(prev => ({ ...prev, images: [...prev.images, ...urls] }));
      } else {
        setEditForm(prev => ({ ...prev, images: [...(prev.images || []), ...urls] }));
      }
    } catch (e) { console.error('Error:', e); }
    finally { setUploadingImage(false); }
  };

  const removeAdditionalImage = (index, isNew = false) => {
    if (isNew) {
      setNewProductForm(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
    } else {
      setEditForm(prev => ({ ...prev, images: (prev.images || []).filter((_, i) => i !== index) }));
    }
  };

  // Product handlers
  const startEditProduct = (product) => {
    setEditingProduct(product);
    setEditForm({
      price: product.price, image: product.image, images: product.images || [],
      inStock: product.inStock, name_hu: product.name?.hu || '', name_en: product.name?.en || '',
      description_hu: product.description?.hu || '', description_en: product.description?.en || '',
      brand: product.brand || '', height: product.height || '', category: product.category || 'hookah',
      subcategory: product.subcategory || null,
      stock: product.stock ?? 1,
      isBestseller: product.isBestseller || false, isSale: product.isSale || false,
      salePrice: product.salePrice || '',
      saleEndDate: product.saleEndDate || '',
      // Specs
      spec_height: product.specs?.height || '',
      spec_brand: product.specs?.brand || '',
      spec_material: product.specs?.material || '',
      spec_water_tank: product.specs?.water_tank_connection || '',
      spec_hose_type: product.specs?.hose_type || '',
    });
  };

  const saveProduct = async () => {
    setSaveLoading(true);
    try {
      const updateData = {
        price: parseInt(editForm.price), image: editForm.image, images: editForm.images || [],
        inStock: editForm.inStock, name: { hu: editForm.name_hu, en: editForm.name_en },
        description: { hu: editForm.description_hu, en: editForm.description_en },
        brand: editForm.brand, height: editForm.height || null, category: editForm.category,
        subcategory: editForm.category === 'accessory' ? (editForm.subcategory || null) : null,
        stock: editForm.stock === '' ? 0 : parseInt(editForm.stock),
        isBestseller: editForm.isBestseller, isSale: editForm.isSale,
        salePrice: editForm.salePrice ? parseInt(editForm.salePrice) : null,
        saleEndDate: editForm.isSale ? (editForm.saleEndDate || null) : null,
      };
      // Add specs for hookah/set
      if (editForm.category === 'hookah' || editForm.category === 'set') {
        updateData.specs = {
          height: editForm.spec_height || null,
          brand: editForm.spec_brand || null,
          material: editForm.spec_material || null,
          water_tank_connection: editForm.spec_water_tank || null,
          hose_type: editForm.spec_hose_type || null,
        };
      }
      await axios.patch(`${API}/products/${editingProduct.id}`, updateData, { headers: { Authorization: `Bearer ${token}` } });
      setEditingProduct(null); fetchData();
    } catch (e) { console.error('Error:', e); }
    finally { setSaveLoading(false); }
  };

  const createProduct = async () => {
    setSaveLoading(true);
    try {
      const productData = {
        name: { hu: newProductForm.name_hu, en: newProductForm.name_en },
        description: { hu: newProductForm.description_hu, en: newProductForm.description_en },
        price: parseInt(newProductForm.price), brand: newProductForm.brand,
        height: newProductForm.height || null, category: newProductForm.category,
        subcategory: newProductForm.category === 'accessory' ? (newProductForm.subcategory || null) : null,
        stock: newProductForm.stock === '' ? 0 : parseInt(newProductForm.stock),
        image: newProductForm.image || `https://placehold.co/400x400/1a1a1a/666666?text=${newProductForm.brand || 'P'}`,
        images: newProductForm.images || [],
        inStock: newProductForm.inStock, isBestseller: newProductForm.isBestseller,
        isSale: newProductForm.isSale, salePrice: newProductForm.salePrice ? parseInt(newProductForm.salePrice) : null,
        saleEndDate: newProductForm.isSale ? (newProductForm.saleEndDate || null) : null,
      };
      // Add specs for hookah/set
      if (newProductForm.category === 'hookah' || newProductForm.category === 'set') {
        productData.specs = {
          height: newProductForm.spec_height || null,
          brand: newProductForm.spec_brand || null,
          material: newProductForm.spec_material || null,
          water_tank_connection: newProductForm.spec_water_tank || null,
          hose_type: newProductForm.spec_hose_type || null,
        };
      }
      await axios.post(`${API}/products`, productData, { headers: { Authorization: `Bearer ${token}` } });
      setShowNewProduct(false);
      setNewProductForm({ name_hu: '', name_en: '', description_hu: '', description_en: '', price: '', brand: '', height: '', category: 'hookah', subcategory: null, inStock: true, isBestseller: false, isSale: false, salePrice: '', saleEndDate: '', image: '', images: [], spec_height: '', spec_brand: '', spec_material: '', spec_water_tank: '', spec_hose_type: '' });
      fetchData();
    } catch (e) { console.error('Error:', e); }
    finally { setSaveLoading(false); }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm(language === 'hu' ? 'Törölni?' : 'Delete?')) return;
    try { await axios.delete(`${API}/products/${productId}`, { headers: { Authorization: `Bearer ${token}` } }); fetchData(); }
    catch (e) { console.error('Error:', e); }
  };

  // Promo handlers
  const createPromoCode = async () => {
    try {
      await axios.post(`${API}/promo-codes`, {
        code: newPromoForm.code.toUpperCase(), discount_type: newPromoForm.discount_type,
        discount_value: parseInt(newPromoForm.discount_value), max_uses: parseInt(newPromoForm.max_uses) || 0, is_active: true,
        min_order: parseInt(newPromoForm.min_order) || 0,
        expires_at: newPromoForm.expires_at || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowNewPromo(false); setNewPromoForm({ code: '', discount_type: 'fixed', discount_value: '', max_uses: 0, min_order: '', expires_at: '' }); fetchData();
    } catch (e) { console.error('Error:', e); }
  };

  const deletePromoCode = async (code) => {
    if (!window.confirm(language === 'hu' ? 'Törölni?' : 'Delete?')) return;
    try { await axios.delete(`${API}/promo-codes/${code}`, { headers: { Authorization: `Bearer ${token}` } }); fetchData(); }
    catch (e) { console.error('Error:', e); }
  };

  const togglePromoActive = async (code, isActive) => {
    try { await axios.patch(`${API}/promo-codes/${code}`, { is_active: !isActive }, { headers: { Authorization: `Bearer ${token}` } }); fetchData(); }
    catch (e) { console.error('Error:', e); }
  };

  const formatDate = (d) => new Date(d).toLocaleDateString(language === 'hu' ? 'hu-HU' : 'en-GB', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Login screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-4">
        <div className="w-full max-w-sm">
          <div className="bg-zinc-900 rounded-xl p-6 md:p-8 border border-zinc-800">
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-3">
                <Lock className="h-7 w-7 text-zinc-400" />
              </div>
              <h1 className="text-xl font-bold text-white">Admin</h1>
            </div>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input type="text" value={loginForm.username} onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={language === 'hu' ? 'Felhasználónév' : 'Username'} data-testid="admin-username-input" />
              <Input type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white" placeholder={language === 'hu' ? 'Jelszó' : 'Password'} data-testid="admin-password-input" />
              {loginError && <p className="text-red-500 text-sm">{loginError}</p>}
              <Button type="submit" className="w-full bg-white text-black hover:bg-zinc-200" disabled={loginLoading} data-testid="admin-login-button">
                {loginLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : (language === 'hu' ? 'Belépés' : 'Login')}
              </Button>
            </form>
            <Button variant="ghost" className="w-full mt-3 text-zinc-400" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-2 h-4 w-4" />{language === 'hu' ? 'Vissza' : 'Back'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Admin Panel
  return (
    <div className="min-h-screen bg-black py-3 md:py-6 px-2 md:px-4">
      <div className="container mx-auto max-w-7xl">
        {/* Shared suggestion lists for product specifications (selectable + free text) */}
        <datalist id="material-options">
          <option value="Rozsdamentes acél" />
        </datalist>
        <datalist id="connection-options">
          <option value="Click" />
          <option value="Menetes" />
        </datalist>
        <datalist id="hose-options">
          <option value="Szilikon" />
        </datalist>
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4 md:mb-6">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" className="text-zinc-400 hover:text-white" onClick={() => navigate('/')}>
              <ArrowLeft className="mr-1 h-4 w-4" />{language === 'hu' ? 'Bolt' : 'Store'}
            </Button>
            <h1 className="text-xl md:text-2xl font-bold text-white">Admin</h1>
          </div>
          <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400" onClick={handleLogout}>
            <LogOut className="mr-1 h-4 w-4" />{language === 'hu' ? 'Ki' : 'Out'}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-4 md:mb-6">
          {[
            { icon: Package, color: 'blue', label: language === 'hu' ? 'Termékek' : 'Products', value: stats.products },
            { icon: ShoppingBag, color: 'green', label: language === 'hu' ? 'Rendelések' : 'Orders', value: stats.orders },
            { icon: Star, color: 'yellow', label: 'Bestseller', value: stats.bestsellers },
            { icon: Sparkles, color: 'orange', label: language === 'hu' ? 'Akciók' : 'Sale', value: stats.sale_products || 0 },
          ].map(({ icon: Icon, color, label, value }) => (
            <div key={label} className="bg-zinc-900 rounded-lg p-3 md:p-4 border border-zinc-800">
              <div className="flex items-center gap-2 md:gap-3">
                <div className={`w-9 h-9 md:w-10 md:h-10 bg-${color}-600/20 rounded-lg flex items-center justify-center`}>
                  <Icon className={`h-4 w-4 md:h-5 md:w-5 text-${color}-500`} />
                </div>
                <div>
                  <p className="text-zinc-500 text-xs">{label}</p>
                  <p className="text-lg md:text-xl font-bold text-white">{value}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="orders" className="space-y-3 md:space-y-4">
          <TabsList className="bg-zinc-900 border border-zinc-800 h-auto p-1 flex-wrap">
            <TabsTrigger value="orders" className="data-[state=active]:bg-zinc-800 text-xs md:text-sm px-3 py-1.5">{language === 'hu' ? 'Rendelések' : 'Orders'}</TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-zinc-800 text-xs md:text-sm px-3 py-1.5">{language === 'hu' ? 'Termékek' : 'Products'}</TabsTrigger>
            <TabsTrigger value="promo" className="data-[state=active]:bg-zinc-800 text-xs md:text-sm px-3 py-1.5">{language === 'hu' ? 'Promo' : 'Promo'}</TabsTrigger>
            <TabsTrigger value="customers" className="data-[state=active]:bg-zinc-800 text-xs md:text-sm px-3 py-1.5" data-testid="admin-tab-customers">{language === 'hu' ? 'Vásárlók' : 'Customers'}</TabsTrigger>
          </TabsList>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <div className="flex flex-wrap gap-2 mb-3">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[120px] md:w-[150px] bg-zinc-800 border-zinc-700 text-white text-sm h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="all" className="text-white">{language === 'hu' ? 'Mind' : 'All'}</SelectItem>
                  {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v[language]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" className="border-zinc-700 text-white h-9" onClick={fetchData}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
              <div className="space-y-2 md:space-y-3 max-h-[60vh] overflow-y-auto">
                {loading ? <div className="py-12 text-center"><RefreshCw className="h-6 w-6 animate-spin text-zinc-500 mx-auto" /></div> :
                  orders.length === 0 ? <div className="py-12 text-center bg-zinc-900 rounded-lg"><Package className="h-10 w-10 text-zinc-600 mx-auto mb-2" /><p className="text-zinc-500 text-sm">{language === 'hu' ? 'Nincs' : 'None'}</p></div> :
                    orders.map((order) => (
                      <div key={order.id} onClick={() => setSelectedOrder(order)} className={`bg-zinc-900 rounded-lg p-3 cursor-pointer transition-all ${selectedOrder?.id === order.id ? 'ring-2 ring-white' : 'hover:bg-zinc-800'}`}>
                        <div className="flex justify-between items-start mb-1">
                          <div><h3 className="font-medium text-white text-sm">{order.name}</h3><p className="text-xs text-zinc-500">#{order.id.slice(0, 8)}</p></div>
                          <Badge className={`${statusLabels[order.status]?.color} text-white text-xs`}>{statusLabels[order.status]?.[language]}</Badge>
                        </div>
                        <div className="flex justify-between items-center text-xs">
                          <div className="flex items-center gap-1">
                            <span className="text-zinc-400">{order.items?.length || 0} db</span>
                            {order.payment_method === 'prepayment' ? <CreditCard className="h-3 w-3 text-zinc-500" /> : <Banknote className="h-3 w-3 text-zinc-500" />}
                          </div>
                          <span className="font-medium text-white">{formatPrice(order.total)} Ft</span>
                        </div>
                        <p className="text-xs text-zinc-500 mt-1"><Clock className="h-3 w-3 inline mr-1" />{formatDate(order.created_at)}</p>
                      </div>
                    ))}
              </div>

              {/* Order Detail */}
              <div className="lg:sticky lg:top-20">
                {selectedOrder ? (
                  <div className="bg-zinc-900 rounded-lg p-3 md:p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div><h2 className="text-base md:text-lg font-bold text-white">{selectedOrder.name}</h2><p className="text-xs text-zinc-500">#{selectedOrder.id}</p></div>
                      <Select value={selectedOrder.status} onValueChange={(v) => updateOrderStatus(selectedOrder.id, v)}>
                        <SelectTrigger className={`w-[100px] md:w-[120px] ${statusLabels[selectedOrder.status]?.color} text-white border-0 text-xs h-8`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {Object.entries(statusLabels).map(([k, v]) => <SelectItem key={k} value={k} className="text-white">{v[language]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5 mb-3 p-2 bg-zinc-800 rounded-lg text-xs">
                      <div className="flex items-center gap-2 text-zinc-300"><Mail className="h-3 w-3" /><a href={`mailto:${selectedOrder.email}`} className="truncate">{selectedOrder.email}</a></div>
                      <div className="flex items-center gap-2 text-zinc-300"><Phone className="h-3 w-3" /><a href={`tel:${selectedOrder.phone}`}>{selectedOrder.phone}</a></div>
                      <div className="flex items-center gap-2 text-zinc-300"><MapPin className="h-3 w-3" /><span>{deliveryLabels[selectedOrder.delivery]?.[language]}</span></div>
                      <div className="flex items-center gap-2 text-zinc-300">{selectedOrder.payment_method === 'prepayment' ? <CreditCard className="h-3 w-3" /> : <Banknote className="h-3 w-3" />}<span>{paymentLabels[selectedOrder.payment_method]?.[language]}</span></div>
                      {selectedOrder.promo_code && <div className="flex items-center gap-2 text-green-400"><Tag className="h-3 w-3" /><span>{selectedOrder.promo_code}</span></div>}
                    </div>
                    <div className="space-y-1.5 mb-3 max-h-32 overflow-y-auto">
                      {selectedOrder.items?.map((item, i) => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-zinc-800 rounded text-xs">
                          {item.image && <div className="w-8 h-8 bg-zinc-700 rounded flex-shrink-0"><img src={item.image} alt="" className="w-full h-full object-contain" /></div>}
                          <div className="flex-1 min-w-0"><p className="text-white truncate">{item.name}</p><p className="text-zinc-500">x{item.quantity}</p></div>
                          <span className="text-white font-medium">{formatPrice(item.price * item.quantity)} Ft</span>
                        </div>
                      ))}
                    </div>
                    <div className="border-t border-zinc-800 pt-2 space-y-1">
                      {selectedOrder.discount > 0 && <>
                        <div className="flex justify-between text-xs"><span className="text-zinc-400">{language === 'hu' ? 'Részösszeg' : 'Subtotal'}</span><span className="text-white">{formatPrice(selectedOrder.subtotal)} Ft</span></div>
                        <div className="flex justify-between text-xs"><span className="text-green-500">{language === 'hu' ? 'Kedvezmény' : 'Discount'}</span><span className="text-green-500">-{formatPrice(selectedOrder.discount)} Ft</span></div>
                      </>}
                      <div className="flex justify-between"><span className="font-medium text-white">{language === 'hu' ? 'Összesen' : 'Total'}</span><span className="font-bold text-white">{formatPrice(selectedOrder.total)} Ft</span></div>
                    </div>
                  </div>
                ) : <div className="bg-zinc-900 rounded-lg p-8 text-center"><Package className="h-10 w-10 text-zinc-600 mx-auto mb-2" /><p className="text-zinc-500 text-sm">{language === 'hu' ? 'Válasszon' : 'Select order'}</p></div>}
              </div>
            </div>
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <div className="flex gap-2 mb-3 flex-wrap items-center">
              <Button size="sm" className="bg-white text-black hover:bg-zinc-200" onClick={() => setShowNewProduct(true)}>
                <Plus className="h-4 w-4 mr-1" />{language === 'hu' ? 'Új' : 'New'}
              </Button>
              <Button variant="outline" size="sm" className="border-zinc-700 text-white" onClick={fetchData}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Select value={productFilter} onValueChange={setProductFilter}>
                <SelectTrigger className="w-[220px] bg-zinc-800 border-zinc-700 text-white text-sm h-9" data-testid="admin-product-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700 max-h-72">
                  {productTypes.map((o) => (
                    <SelectItem key={o.value} value={o.value} className="text-white">{o.label[language] || o.label.hu}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="bg-zinc-900 rounded-lg border border-zinc-800 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-800 text-xs text-zinc-400">
                    <th className="text-left p-2 md:p-3">{language === 'hu' ? 'Termék' : 'Product'}</th>
                    <th className="text-left p-2 md:p-3 hidden md:table-cell">{language === 'hu' ? 'Kat.' : 'Cat.'}</th>
                    <th className="text-left p-2 md:p-3">{language === 'hu' ? 'Ár' : 'Price'}</th>
                    <th className="text-left p-2 md:p-3">{language === 'hu' ? 'Készlet' : 'Stock'}</th>
                    <th className="text-left p-2 md:p-3 hidden sm:table-cell">{language === 'hu' ? 'Képek' : 'Images'}</th>
                    <th className="text-right p-2 md:p-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {products.filter((p) => getTypeValue(p.category, p.subcategory) === productFilter).map((p) => (
                    <tr key={p.id} className="border-b border-zinc-800 hover:bg-zinc-800/50">
                      <td className="p-2 md:p-3">
                        <div className="flex items-center gap-2">
                          <div className="w-10 h-10 bg-zinc-800 rounded flex-shrink-0"><img src={p.image} alt="" className="w-full h-full object-contain" onError={(e) => { e.target.src = 'https://placehold.co/40x40/1a1a1a/666?text=P'; }} /></div>
                          <div className="min-w-0">
                            <p className="text-white font-medium truncate max-w-[100px] md:max-w-[180px] text-xs md:text-sm">{p.name?.[language] || p.name?.en}</p>
                            <p className="text-xs text-zinc-500">{p.brand}</p>
                            <div className="flex gap-1">{p.isBestseller && <Star className="h-3 w-3 text-yellow-500" />}{p.isSale && <Sparkles className="h-3 w-3 text-orange-500" />}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-2 md:p-3 hidden md:table-cell"><Badge variant="outline" className="text-zinc-400 border-zinc-700 text-xs">{categoryLabels[p.category]?.[language]}</Badge></td>
                      <td className="p-2 md:p-3 text-white font-medium text-xs md:text-sm">{formatPrice(p.price)}</td>
                      <td className="p-2 md:p-3 text-xs md:text-sm" data-testid={`stock-${p.id}`}><span className={(p.stock ?? 0) <= 0 ? 'text-red-400 font-semibold' : 'text-white'}>{p.stock ?? 0}</span></td>
                      <td className="p-2 md:p-3 text-zinc-400 text-xs hidden sm:table-cell">{1 + (p.images?.length || 0)}</td>
                      <td className="p-2 md:p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" className="border-zinc-700 text-zinc-400 h-7 px-2" onClick={() => startEditProduct(p)}><Edit2 className="h-3 w-3" /></Button>
                          <Button size="sm" variant="outline" className="border-zinc-700 text-red-400 h-7 px-2" onClick={() => deleteProduct(p.id)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* Promo Tab */}
          <TabsContent value="promo">
            <div className="flex gap-2 mb-3">
              <Button size="sm" className="bg-white text-black hover:bg-zinc-200" onClick={() => setShowNewPromo(true)}>
                <Plus className="h-4 w-4 mr-1" />{language === 'hu' ? 'Új' : 'New'}
              </Button>
            </div>

            <div className="bg-zinc-900 rounded-lg border border-zinc-800">
              {promoCodes.length === 0 ? <div className="p-8 text-center"><Tag className="h-10 w-10 text-zinc-600 mx-auto mb-2" /><p className="text-zinc-500 text-sm">{language === 'hu' ? 'Nincs' : 'None'}</p></div> :
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-zinc-800 text-xs text-zinc-400">
                        <th className="text-left p-2 md:p-3">{language === 'hu' ? 'Kód' : 'Code'}</th>
                        <th className="text-left p-2 md:p-3">{language === 'hu' ? 'Kedv.' : 'Disc.'}</th>
                        <th className="text-left p-2 md:p-3 hidden sm:table-cell">{language === 'hu' ? 'Haszn.' : 'Used'}</th>
                        <th className="text-left p-2 md:p-3 hidden md:table-cell">{language === 'hu' ? 'Min.' : 'Min.'}</th>
                        <th className="text-left p-2 md:p-3 hidden md:table-cell">{language === 'hu' ? 'Lejárat' : 'Expiry'}</th>
                        <th className="text-left p-2 md:p-3">{language === 'hu' ? 'Aktív' : 'Active'}</th>
                        <th className="text-right p-2 md:p-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {promoCodes.map((pc) => (
                        <tr key={pc.id} className="border-b border-zinc-800">
                          <td className="p-2 md:p-3"><code className="text-white font-mono bg-zinc-800 px-1.5 py-0.5 rounded text-xs">{pc.code}</code></td>
                          <td className="p-2 md:p-3 text-white text-xs">{pc.discount_type === 'percentage' ? `${pc.discount_value}%` : `${formatPrice(pc.discount_value)} Ft`}</td>
                          <td className="p-2 md:p-3 text-zinc-400 text-xs hidden sm:table-cell">{pc.current_uses}/{pc.max_uses === 0 ? '∞' : pc.max_uses}</td>
                          <td className="p-2 md:p-3 text-zinc-400 text-xs hidden md:table-cell">{pc.min_order ? `${formatPrice(pc.min_order)} Ft` : '—'}</td>
                          <td className="p-2 md:p-3 text-zinc-400 text-xs hidden md:table-cell">{pc.expires_at ? new Date(pc.expires_at).toLocaleDateString(language === 'hu' ? 'hu-HU' : 'en-GB') : '∞'}</td>
                          <td className="p-2 md:p-3"><Switch checked={pc.is_active} onCheckedChange={() => togglePromoActive(pc.code, pc.is_active)} /></td>
                          <td className="p-2 md:p-3 text-right"><Button size="sm" variant="outline" className="border-zinc-700 text-red-400 h-7 px-2" onClick={() => deletePromoCode(pc.code)}><Trash2 className="h-3 w-3" /></Button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>}
            </div>
          </TabsContent>

          {/* Customers Tab */}
          <TabsContent value="customers">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm text-zinc-400">{language === 'hu' ? `Regisztrált vásárlók: ${customers.length}` : `Registered customers: ${customers.length}`}</p>
              <Button variant="outline" size="sm" className="border-zinc-700 text-white h-9" onClick={fetchData}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            {customers.length === 0 ? (
              <div className="py-12 text-center bg-zinc-900 rounded-lg">
                <Users className="h-10 w-10 text-zinc-600 mx-auto mb-2" />
                <p className="text-zinc-500 text-sm">{language === 'hu' ? 'Még nincs regisztrált vásárló.' : 'No registered customers yet.'}</p>
              </div>
            ) : (
              <div className="bg-zinc-900 rounded-lg overflow-x-auto" data-testid="admin-customers-table">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-zinc-400 text-left text-xs">
                      <th className="px-3 py-2">{language === 'hu' ? 'Név' : 'Name'}</th>
                      <th className="px-3 py-2">Email</th>
                      <th className="px-3 py-2">{language === 'hu' ? 'Telefon' : 'Phone'}</th>
                      <th className="px-3 py-2 text-center">{language === 'hu' ? 'Rendelések' : 'Orders'}</th>
                      <th className="px-3 py-2 text-right">{language === 'hu' ? 'Összes költés' : 'Total spent'}</th>
                      <th className="px-3 py-2 text-center">{language === 'hu' ? 'Következő kedv.' : 'Next disc.'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {customers.map((c) => (
                      <tr key={c.id} className="border-b border-zinc-800/60 text-zinc-300" data-testid={`admin-customer-row-${c.email}`}>
                        <td className="px-3 py-2 text-white font-medium">{c.name}</td>
                        <td className="px-3 py-2"><a href={`mailto:${c.email}`} className="hover:text-white">{c.email}</a></td>
                        <td className="px-3 py-2">{c.phone ? <a href={`tel:${c.phone}`} className="hover:text-white">{c.phone}</a> : <span className="text-zinc-600">—</span>}</td>
                        <td className="px-3 py-2 text-center">{c.orders_count}</td>
                        <td className="px-3 py-2 text-right text-white">{formatPrice(c.total_spent)} Ft</td>
                        <td className="px-3 py-2 text-center">
                          {c.loyalty_percent > 0
                            ? <Badge className="bg-amber-600 text-white text-xs">{c.loyalty_percent}%</Badge>
                            : <span className="text-zinc-600">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Product Dialog */}
        <Dialog open={!!editingProduct} onOpenChange={() => setEditingProduct(null)}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg md:max-w-2xl max-h-[90vh] overflow-y-auto mx-2">
            <DialogHeader><DialogTitle className="text-base md:text-lg">{language === 'hu' ? 'Szerkesztés' : 'Edit Product'}</DialogTitle></DialogHeader>
            {editingProduct && (
              <div className="space-y-3">
                {/* Main Image */}
                <div>
                  <Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Fő kép' : 'Main Image'}</Label>
                  <div className="mt-1 flex items-start gap-3">
                    {editForm.image && <div className="w-20 h-20 bg-zinc-800 rounded flex-shrink-0"><img src={editForm.image} alt="" className="w-full h-full object-contain" /></div>}
                    <div className="flex-1">
                      <input type="file" ref={fileInputRef} accept="image/*" className="hidden" onChange={(e) => handleMainImageUpload(e.target.files[0], false)} />
                      <Button type="button" variant="outline" size="sm" className="border-zinc-700 text-white" onClick={() => fileInputRef.current?.click()} disabled={uploadingImage}>
                        {uploadingImage ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{language === 'hu' ? 'Feltöltés' : 'Upload'}
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Additional Images */}
                <div>
                  <Label className="text-zinc-400 text-xs">{language === 'hu' ? 'További képek' : 'Additional Images'}</Label>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {(editForm.images || []).map((img, i) => (
                      <div key={i} className="relative w-16 h-16 bg-zinc-800 rounded">
                        <img src={img} alt="" className="w-full h-full object-contain rounded" />
                        <button onClick={() => removeAdditionalImage(i, false)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center"><X className="h-3 w-3 text-white" /></button>
                      </div>
                    ))}
                    <input type="file" ref={additionalImagesRef} accept="image/*" multiple className="hidden" onChange={(e) => handleAdditionalImageUpload(e.target.files, false)} />
                    <button onClick={() => additionalImagesRef.current?.click()} disabled={uploadingImage} className="w-16 h-16 border-2 border-dashed border-zinc-700 rounded flex items-center justify-center text-zinc-500 hover:border-zinc-500 hover:text-zinc-400">
                      {uploadingImage ? <RefreshCw className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Név (HU)' : 'Name HU'}</Label><Input value={editForm.name_hu} onChange={(e) => setEditForm({ ...editForm, name_hu: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Név (EN)' : 'Name EN'}</Label><Input value={editForm.name_en} onChange={(e) => setEditForm({ ...editForm, name_en: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Ár (HUF)' : 'Price'}</Label><Input type="number" value={editForm.price} onChange={(e) => setEditForm({ ...editForm, price: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Márka' : 'Brand'}</Label><Input value={editForm.brand} onChange={(e) => setEditForm({ ...editForm, brand: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Kategória' : 'Category'}</Label>
                    <Select value={getTypeValue(editForm.category, editForm.subcategory)} onValueChange={(v) => handleTypeChange(v, false)}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700 max-h-72">
                        {productTypes.map((o) => (
                          <SelectItem key={o.value} value={o.value} className="text-white">{o.label[language] || o.label.hu}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Magasság' : 'Height'}</Label><Input value={editForm.height} onChange={(e) => setEditForm({ ...editForm, height: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="65 cm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Készlet (db)' : 'Stock (pcs)'}</Label><Input type="number" min="0" value={editForm.stock} onChange={(e) => setEditForm({ ...editForm, stock: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" data-testid="edit-stock-input" /></div>
                </div>

                <div className="flex flex-wrap gap-4">
                  <div className="flex items-center gap-2"><Switch checked={editForm.inStock} onCheckedChange={(c) => setEditForm({ ...editForm, inStock: c })} /><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Készleten' : 'In Stock'}</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={editForm.isBestseller} onCheckedChange={(c) => setEditForm({ ...editForm, isBestseller: c })} /><Label className="text-zinc-400 text-xs flex items-center gap-1"><Star className="h-3 w-3 text-yellow-500" />Best</Label></div>
                  <div className="flex items-center gap-2"><Switch checked={editForm.isSale} onCheckedChange={(c) => setEditForm({ ...editForm, isSale: c })} /><Label className="text-zinc-400 text-xs flex items-center gap-1"><Sparkles className="h-3 w-3 text-orange-500" />{language === 'hu' ? 'Akció' : 'Sale'}</Label></div>
                </div>

                {editForm.isSale && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Akciós ár' : 'Sale Price'}</Label><Input type="number" value={editForm.salePrice} onChange={(e) => setEditForm({ ...editForm, salePrice: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                    <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Akció vége' : 'Sale ends'}</Label><Input type="date" value={editForm.saleEndDate} onChange={(e) => setEditForm({ ...editForm, saleEndDate: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                  </div>
                )}

                {/* Specifications for hookah/set */}
                {(editForm.category === 'hookah' || editForm.category === 'set') && (
                  <div className="border border-zinc-700 rounded-lg p-3 space-y-3">
                    <Label className="text-zinc-300 text-sm font-medium">{language === 'hu' ? 'Specifikációk' : 'Specifications'}</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Magasság' : 'Height'}</Label><Input value={editForm.spec_height} onChange={(e) => setEditForm({ ...editForm, spec_height: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="65 cm" /></div>
                      <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Márka' : 'Brand'}</Label><Input value={editForm.spec_brand} onChange={(e) => setEditForm({ ...editForm, spec_brand: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Alpha Hookah" /></div>
                    </div>
                    <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Anyag' : 'Material'}</Label><Input list="material-options" value={editForm.spec_material} onChange={(e) => setEditForm({ ...editForm, spec_material: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Rozsdamentes acél" /></div>
                    <div><Label className="text-zinc-400 text-xs">Víztartály csatlakozás</Label><Input list="connection-options" value={editForm.spec_water_tank} onChange={(e) => setEditForm({ ...editForm, spec_water_tank: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Click / Menetes" /></div>
                    <div><Label className="text-zinc-400 text-xs">Gyári csövek tipusa</Label><Input list="hose-options" value={editForm.spec_hose_type} onChange={(e) => setEditForm({ ...editForm, spec_hose_type: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Szilikon" /></div>
                  </div>
                )}

                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Leírás (HU)' : 'Desc HU'}</Label><Textarea value={editForm.description_hu} onChange={(e) => setEditForm({ ...editForm, description_hu: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 text-sm" rows={2} /></div>
                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Leírás (EN)' : 'Desc EN'}</Label><Textarea value={editForm.description_en} onChange={(e) => setEditForm({ ...editForm, description_en: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 text-sm" rows={2} /></div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400" onClick={() => setEditingProduct(null)}>{language === 'hu' ? 'Mégse' : 'Cancel'}</Button>
              <Button size="sm" className="bg-white text-black" onClick={saveProduct} disabled={saveLoading}>{saveLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" />{language === 'hu' ? 'Mentés' : 'Save'}</>}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Product Dialog */}
        <Dialog open={showNewProduct} onOpenChange={setShowNewProduct}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg md:max-w-2xl max-h-[90vh] overflow-y-auto mx-2">
            <DialogHeader><DialogTitle className="text-base md:text-lg">{language === 'hu' ? 'Új termék' : 'New Product'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {/* Main Image */}
              <div>
                <Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Fő kép' : 'Main Image'}</Label>
                <div className="mt-1 flex items-start gap-3">
                  {newProductForm.image && <div className="w-20 h-20 bg-zinc-800 rounded flex-shrink-0"><img src={newProductForm.image} alt="" className="w-full h-full object-contain" /></div>}
                  <div className="flex-1">
                    <input type="file" ref={newProductImageRef} accept="image/*" className="hidden" onChange={(e) => handleMainImageUpload(e.target.files[0], true)} />
                    <Button type="button" variant="outline" size="sm" className="border-zinc-700 text-white" onClick={() => newProductImageRef.current?.click()} disabled={uploadingImage}>
                      {uploadingImage ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 mr-1" />}{language === 'hu' ? 'Feltöltés' : 'Upload'}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Additional Images */}
              <div>
                <Label className="text-zinc-400 text-xs">{language === 'hu' ? 'További képek' : 'Additional Images'}</Label>
                <div className="mt-1 flex flex-wrap gap-2">
                  {(newProductForm.images || []).map((img, i) => (
                    <div key={i} className="relative w-16 h-16 bg-zinc-800 rounded">
                      <img src={img} alt="" className="w-full h-full object-contain rounded" />
                      <button onClick={() => removeAdditionalImage(i, true)} className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 rounded-full flex items-center justify-center"><X className="h-3 w-3 text-white" /></button>
                    </div>
                  ))}
                  <input type="file" ref={newProductAdditionalRef} accept="image/*" multiple className="hidden" onChange={(e) => handleAdditionalImageUpload(e.target.files, true)} />
                  <button onClick={() => newProductAdditionalRef.current?.click()} disabled={uploadingImage} className="w-16 h-16 border-2 border-dashed border-zinc-700 rounded flex items-center justify-center text-zinc-500 hover:border-zinc-500 hover:text-zinc-400">
                    {uploadingImage ? <RefreshCw className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Név (HU)' : 'Name HU'} *</Label><Input value={newProductForm.name_hu} onChange={(e) => setNewProductForm({ ...newProductForm, name_hu: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Név (EN)' : 'Name EN'} *</Label><Input value={newProductForm.name_en} onChange={(e) => setNewProductForm({ ...newProductForm, name_en: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Ár (HUF)' : 'Price'} *</Label><Input type="number" value={newProductForm.price} onChange={(e) => setNewProductForm({ ...newProductForm, price: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Márka' : 'Brand'} *</Label><Input value={newProductForm.brand} onChange={(e) => setNewProductForm({ ...newProductForm, brand: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Kategória' : 'Category'}</Label>
                  <Select value={getTypeValue(newProductForm.category, newProductForm.subcategory)} onValueChange={(v) => handleTypeChange(v, true)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700 max-h-72">
                      {productTypes.map((o) => (
                        <SelectItem key={o.value} value={o.value} className="text-white">{o.label[language] || o.label.hu}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Magasság' : 'Height'}</Label><Input value={newProductForm.height} onChange={(e) => setNewProductForm({ ...newProductForm, height: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="65 cm" /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Készlet (db)' : 'Stock (pcs)'}</Label><Input type="number" min="0" value={newProductForm.stock} onChange={(e) => setNewProductForm({ ...newProductForm, stock: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" data-testid="new-stock-input" /></div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2"><Switch checked={newProductForm.inStock} onCheckedChange={(c) => setNewProductForm({ ...newProductForm, inStock: c })} /><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Készleten' : 'In Stock'}</Label></div>
                <div className="flex items-center gap-2"><Switch checked={newProductForm.isBestseller} onCheckedChange={(c) => setNewProductForm({ ...newProductForm, isBestseller: c })} /><Label className="text-zinc-400 text-xs flex items-center gap-1"><Star className="h-3 w-3 text-yellow-500" />Best</Label></div>
                <div className="flex items-center gap-2"><Switch checked={newProductForm.isSale} onCheckedChange={(c) => setNewProductForm({ ...newProductForm, isSale: c })} /><Label className="text-zinc-400 text-xs flex items-center gap-1"><Sparkles className="h-3 w-3 text-orange-500" />{language === 'hu' ? 'Akció' : 'Sale'}</Label></div>
              </div>

              {newProductForm.isSale && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Akciós ár' : 'Sale Price'}</Label><Input type="number" value={newProductForm.salePrice} onChange={(e) => setNewProductForm({ ...newProductForm, salePrice: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Akció vége' : 'Sale ends'}</Label><Input type="date" value={newProductForm.saleEndDate} onChange={(e) => setNewProductForm({ ...newProductForm, saleEndDate: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" /></div>
                </div>
              )}

              {/* Specifications for hookah/set */}
              {(newProductForm.category === 'hookah' || newProductForm.category === 'set') && (
                <div className="border border-zinc-700 rounded-lg p-3 space-y-3">
                  <Label className="text-zinc-300 text-sm font-medium">{language === 'hu' ? 'Specifikációk' : 'Specifications'}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Magasság' : 'Height'}</Label><Input value={newProductForm.spec_height} onChange={(e) => setNewProductForm({ ...newProductForm, spec_height: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="65 cm" /></div>
                    <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Márka' : 'Brand'}</Label><Input value={newProductForm.spec_brand} onChange={(e) => setNewProductForm({ ...newProductForm, spec_brand: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Alpha Hookah" /></div>
                  </div>
                  <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Anyag' : 'Material'}</Label><Input list="material-options" value={newProductForm.spec_material} onChange={(e) => setNewProductForm({ ...newProductForm, spec_material: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Rozsdamentes acél" /></div>
                  <div><Label className="text-zinc-400 text-xs">Víztartály csatlakozás</Label><Input list="connection-options" value={newProductForm.spec_water_tank} onChange={(e) => setNewProductForm({ ...newProductForm, spec_water_tank: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Click / Menetes" /></div>
                  <div><Label className="text-zinc-400 text-xs">Gyári csövek tipusa</Label><Input list="hose-options" value={newProductForm.spec_hose_type} onChange={(e) => setNewProductForm({ ...newProductForm, spec_hose_type: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 text-sm" placeholder="Szilikon" /></div>
                </div>
              )}

              <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Leírás (HU)' : 'Desc HU'}</Label><Textarea value={newProductForm.description_hu} onChange={(e) => setNewProductForm({ ...newProductForm, description_hu: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 text-sm" rows={2} /></div>
              <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Leírás (EN)' : 'Desc EN'}</Label><Textarea value={newProductForm.description_en} onChange={(e) => setNewProductForm({ ...newProductForm, description_en: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 text-sm" rows={2} /></div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400" onClick={() => setShowNewProduct(false)}>{language === 'hu' ? 'Mégse' : 'Cancel'}</Button>
              <Button size="sm" className="bg-white text-black" onClick={createProduct} disabled={saveLoading || !newProductForm.name_hu || !newProductForm.name_en || !newProductForm.price || !newProductForm.brand}>
                {saveLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" />{language === 'hu' ? 'Létrehozás' : 'Create'}</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Promo Dialog */}
        <Dialog open={showNewPromo} onOpenChange={setShowNewPromo}>
          <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-sm mx-2">
            <DialogHeader><DialogTitle className="text-base">{language === 'hu' ? 'Új promókód' : 'New Promo'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Kód' : 'Code'} *</Label><Input value={newPromoForm.code} onChange={(e) => setNewPromoForm({ ...newPromoForm, code: e.target.value.toUpperCase() })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9 font-mono" placeholder="SUMMER24" /></div>
              <div>
                <Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Típus' : 'Type'}</Label>
                <Select value={newPromoForm.discount_type} onValueChange={(v) => setNewPromoForm({ ...newPromoForm, discount_type: v })}>
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="fixed" className="text-white">{language === 'hu' ? 'Fix (HUF)' : 'Fixed (HUF)'}</SelectItem>
                    <SelectItem value="percentage" className="text-white">{language === 'hu' ? 'Százalék (%)' : 'Percent (%)'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-zinc-400 text-xs">{newPromoForm.discount_type === 'percentage' ? '%' : 'HUF'}</Label><Input type="number" value={newPromoForm.discount_value} onChange={(e) => setNewPromoForm({ ...newPromoForm, discount_value: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9" placeholder={newPromoForm.discount_type === 'percentage' ? '10' : '5000'} /></div>
              <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Max haszn. (0=∞)' : 'Max uses (0=∞)'}</Label><Input type="number" value={newPromoForm.max_uses} onChange={(e) => setNewPromoForm({ ...newPromoForm, max_uses: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9" /></div>
              <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Min. rendelés (Ft, 0=nincs)' : 'Min. order (HUF, 0=none)'}</Label><Input type="number" value={newPromoForm.min_order} onChange={(e) => setNewPromoForm({ ...newPromoForm, min_order: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9" placeholder="20000" /></div>
              <div><Label className="text-zinc-400 text-xs">{language === 'hu' ? 'Érvényes eddig' : 'Valid until'}</Label><Input type="date" value={newPromoForm.expires_at} onChange={(e) => setNewPromoForm({ ...newPromoForm, expires_at: e.target.value })} className="bg-zinc-800 border-zinc-700 text-white mt-1 h-9" /></div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" size="sm" className="border-zinc-700 text-zinc-400" onClick={() => setShowNewPromo(false)}>{language === 'hu' ? 'Mégse' : 'Cancel'}</Button>
              <Button size="sm" className="bg-white text-black" onClick={createPromoCode} disabled={!newPromoForm.code || !newPromoForm.discount_value}><Plus className="h-4 w-4 mr-1" />{language === 'hu' ? 'Létrehozás' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminPage;
